import type { TriggerConditionClause } from "./workflow-graph";

/**
 * Condition clauses over an event, in the format workflow trigger nodes store
 * (`TriggerConditionClause`). One evaluator serves a workflow trigger and an
 * agent listener on the same event; what a field means stays with the event
 * type that owns it.
 */

export const CONDITION_OPERATORS = ["eq", "neq", "contains", "not_contains"] as const;
export type ConditionOperator = (typeof CONDITION_OPERATORS)[number];

/** Number comparisons. A side that does not read as a number makes the clause false. */
export const COMPARISON_OPERATORS = ["gt", "gte", "lt", "lte"] as const;
export type ComparisonOperator = (typeof COMPARISON_OPERATORS)[number];

/** Whether there is a value at all (blank text counts as none); these take no right-hand value. */
export const PRESENCE_OPERATORS = ["is_null", "is_not_null"] as const;
export type PresenceOperator = (typeof PRESENCE_OPERATORS)[number];

export function isPresenceOperator(operator: unknown): operator is PresenceOperator {
  return (
    typeof operator === "string" && (PRESENCE_OPERATORS as readonly string[]).includes(operator)
  );
}

// Older graphs spell two operators out. One name each from here on, so every reader agrees.
const OPERATOR_SPELLINGS: Readonly<Record<string, string>> = { equals: "eq", not_equals: "neq" };

/** The operator as the evaluator knows it, or null when it is not a string. */
export function canonicalConditionOperator(operator: unknown): string | null {
  if (typeof operator !== "string") return null;
  return OPERATOR_SPELLINGS[operator] ?? operator;
}

/** A subject's condition fields after an event: field name to value, null when unset. */
export type ConditionState = Record<string, string | null>;

/**
 * Clauses read left to right. `and` binds tighter than `or`: a run of `and`
 * clauses is one group, and the list is true when any group is.
 */
export function evaluateConditionGroups<T extends Pick<TriggerConditionClause, "connector">>(
  clauses: readonly T[],
  matchClause: (clause: T) => boolean,
): boolean {
  if (clauses.length === 0) return true;
  let result = false;
  let currentAndGroup = matchClause(clauses[0]!);
  for (let index = 1; index < clauses.length; index += 1) {
    const clause = clauses[index]!;
    const next = matchClause(clause);
    if (clause.connector === "or") {
      result = result || currentAndGroup;
      currentAndGroup = next;
      continue;
    }
    currentAndGroup = currentAndGroup && next;
  }
  return result || currentAndGroup;
}

/** The `and` groups of a clause list, in order: the list is true when any one of them is. */
export function conditionGroups<T extends Pick<TriggerConditionClause, "connector">>(
  clauses: readonly T[],
): T[][] {
  const groups: T[][] = [];
  clauses.forEach((clause, index) => {
    if (index === 0 || clause.connector === "or") groups.push([clause]);
    else groups.at(-1)!.push(clause);
  });
  return groups;
}

/** Compares two already-normalized strings. An operator outside the list never matches. */
export function matchScalarOperator(operator: string, left: string, right: string): boolean {
  switch (operator) {
    case "eq":
      return left === right;
    case "neq":
      return left !== right;
    case "contains":
      return left.includes(right);
    case "not_contains":
      return !left.includes(right);
    case "gt":
    case "gte":
    case "lt":
    case "lte": {
      const a = readNumber(left);
      const b = readNumber(right);
      if (a === null || b === null) return false;
      if (operator === "gt") return a > b;
      if (operator === "gte") return a >= b;
      return operator === "lt" ? a < b : a <= b;
    }
    default:
      return false;
  }
}

// Number("") is 0, which would make an unset value compare as zero.
function readNumber(value: string): number | null {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

const normalize = (value: unknown) =>
  (typeof value === "string" ? value : String(value ?? "")).toLowerCase();

/**
 * One clause's operator against a raw value: the single place a stored
 * condition is decided, for a workflow trigger, a workflow branch and an agent
 * listener alike. Text is compared without regard to case.
 */
export function matchConditionValue(
  operator: unknown,
  actual: unknown,
  expected: unknown,
): boolean {
  const canonical = canonicalConditionOperator(operator);
  if (canonical === null) return false;
  const empty = actual == null || (typeof actual === "string" && actual.trim() === "");
  if (canonical === "is_null") return empty;
  if (canonical === "is_not_null") return !empty;
  // Nothing at that path satisfies no comparison, or "does not contain" would hold for missing data.
  // A null is a value that is there and unset, and compares as empty text.
  if (actual === undefined) return false;
  return matchScalarOperator(canonical, normalize(actual), normalize(expected));
}

/** Whether the clauses hold on a subject's state. A clause on a field the state does not have is false. */
export function matchesConditions(
  clauses: readonly TriggerConditionClause[],
  state: ConditionState,
): boolean {
  return evaluateConditionGroups(clauses, (clause) => {
    if (!(clause.field in state)) return false;
    return matchConditionValue(clause.operator, state[clause.field], clause.value);
  });
}

/**
 * Whether an event satisfies a listener's conditions. They describe the
 * subject after the event, and the event must have changed a field they name:
 * "status is done" fires on the change that made it true, not on a later
 * edit of some other field. With `or`, the group that holds must be one the
 * event touched: "status is done or priority is high" stays quiet when a done
 * task's priority drops. An arrival (an email, a comment) counts as changing
 * every field. No conditions means any event.
 */
export function eventSatisfies(
  clauses: readonly TriggerConditionClause[],
  event: { changed?: readonly string[] | undefined; arrival: boolean },
  stateAfter: ConditionState,
): boolean {
  if (clauses.length === 0) return true;
  const changed = new Set(event.changed ?? []);
  return conditionGroups(clauses).some(
    (group) =>
      matchesConditions(group, stateAfter) &&
      (event.arrival || group.some((clause) => changed.has(clause.field))),
  );
}
